import { z } from 'zod';
import { createPaginatedResponseSchema } from './common.js';
// =============================================================================
// Payment
// =============================================================================
export const PaymentStatusSchema = z.enum(['PENDING', 'SUCCESS', 'FAILED']);
export const PaymentSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    reference: z.string(),
    amountMinorUnits: z.number(),
    currency: z.string(),
    status: PaymentStatusSchema,
    payerEmail: z.string(),
    channel: z.string().nullable(),
    cardLast4: z.string().nullable(),
    cardBrand: z.string().nullable(),
    authorizationUrl: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const PaymentListResponseSchema = createPaginatedResponseSchema(PaymentSchema);
// =============================================================================
// Subscription
// =============================================================================
export const SubscriptionStatusSchema = z.enum(['TRIALING', 'PENDING_PAYMENT_CONFIRMATION', 'ACTIVE', 'PAST_DUE', 'CANCELLING', 'CANCELLED', 'EXPIRED']);
export const SubscriptionBillingModeSchema = z.enum(['CARD', 'EFT']);
export const SubscriptionPlanSchema = z.object({
    id: z.string(),
    name: z.string(),
    displayName: z.string(),
    amountMinorUnits: z.number(),
    currency: z.string(),
    interval: z.string(),
    limits: z.object({
        maxMembers: z.number(),
        maxSttMinutesPerPeriod: z.number(),
        maxLlmWeightedTokensPerPeriod: z.number(),
    }),
    features: z.object({
        canExportPdf: z.boolean(),
    }),
    sortOrder: z.number(),
});
export const SubscriptionPlanListParamsSchema = z.object({ accountId: z.string() });
export const SubscriptionPlanListResponseSchema = z.array(SubscriptionPlanSchema);
export const SubscriptionSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    status: SubscriptionStatusSchema,
    billingMode: SubscriptionBillingModeSchema,
    providerSubscriptionId: z.string().nullable(),
    plan: SubscriptionPlanSchema,
    currentPeriodStart: z.string().nullable(),
    currentPeriodEnd: z.string().nullable(),
    trialEnd: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    pastDueSince: z.string().nullable(),
    // Derived, never persisted: true while a CANCELLING subscription's paid
    // period has not elapsed and either there is no provider subscription to
    // reconcile, or the customer's own cancel is why it stopped renewing.
    // Always false outside CANCELLING. See `isSubscriptionReactivatable`.
    reactivatable: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SubscriptionResponseSchema = z.object({
    subscription: SubscriptionSchema.nullable(),
    hasSubscriptionHistory: z.boolean(),
});
export const AdminSubscriptionHistoryItemSchema = z.object({
    id: z.string(),
    status: SubscriptionStatusSchema,
    planId: z.string(),
    trialEnd: z.string().nullable(),
    currentPeriodStart: z.string().nullable(),
    currentPeriodEnd: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminSubscriptionHistoryResponseSchema = z.array(AdminSubscriptionHistoryItemSchema);
export const UpdateTrialEndInputSchema = z.object({
    trialEnd: z.iso.datetime({
        error: 'Must be a valid ISO 8601 date-time'
    }),
});
export const RenewTrialInputSchema = z.object({
    trialEnd: z.iso.datetime({
        error: 'Must be a valid ISO 8601 date-time'
    }),
});
export const InitializeCheckoutInputSchema = z.object({
    planId: z.string().min(1, 'Plan ID is required'),
    callbackUrl: z.url('Please provide a valid callback URL'),
});
export const ChangePlanInputSchema = z.object({
    planId: z.string().min(1),
    idempotencyKey: z.string().min(1),
});
export const SwitchToEftInputSchema = z.object({
    idempotencyKey: z.string().min(1),
});
export const ActivateTrialAsEftInputSchema = z.object({
    planId: z.string().min(1),
    idempotencyKey: z.string().min(1),
});
/**
 * When a cancel takes effect. `AT_PERIOD_END` keeps a paid period's access
 * until it ends (what a customer's own cancel does); `IMMEDIATELY` makes the
 * account read-only now, for a period that has been credited or never paid.
 */
export const SubscriptionCancellationTimingSchema = z.enum(['AT_PERIOD_END', 'IMMEDIATELY']);
export const AdminCancelSubscriptionInputSchema = z.strictObject({
    timing: SubscriptionCancellationTimingSchema,
    idempotencyKey: z.string().min(1),
});
export const RetryPaymentInputSchema = z.object({
    idempotencyKey: z.string().min(1),
});
/**
 * Number of calendar days a PAST_DUE subscription retains write access
 * while the customer has a chance to retry their payment.
 * Shared between frontend and backend so both surfaces agree on the grace window.
 */
export const GRACE_DAYS = 7;
export const InitializeCheckoutResponseSchema = z.object({
    authorizationUrl: z.string(),
});
// =============================================================================
// Super Admin - business metrics
// =============================================================================
/**
 * Window (in days) for the "trials ending soon" metric: TRIALING subscriptions
 * whose trialEnd falls within the next this-many days. These are the live
 * conversion opportunities for the admin to act on.
 */
export const TRIALS_ENDING_SOON_DAYS = 7;
/**
 * Top-line business-performance metrics for the super-admin dashboard.
 * All monetary values are in minor units (cents) of {@link AdminBusinessMetricsResponseSchema.shape.currency}.
 *
 * MRR and payingSubscribers count subscriptions currently entitled to the
 * product and expected to generate recurring revenue: ACTIVE (billing
 * normally), CANCELLING (won't renew but still within a paid period) and
 * PAST_DUE (payment failing but still inside the grace window). Trial,
 * pending-payment, cancelled and expired subscriptions are excluded.
 */
export const AdminBusinessMetricsResponseSchema = z.object({
    currency: z.string(),
    /** Monthly recurring revenue across all revenue-generating subscriptions. */
    mrrMinorUnits: z.number(),
    /** Annual recurring revenue (mrrMinorUnits * 12). */
    arrMinorUnits: z.number(),
    /** Average revenue per paying account (mrrMinorUnits / payingSubscribers); 0 when there are none. */
    arpaMinorUnits: z.number(),
    /** Accounts currently generating recurring revenue (ACTIVE + CANCELLING + PAST_DUE). */
    payingSubscribers: z.number(),
    /** Accounts on an active trial (TRIALING). */
    trialingAccounts: z.number(),
    /** Trials whose trialEnd falls within the next {@link TRIALS_ENDING_SOON_DAYS} days. */
    trialsEndingSoon: z.number(),
});
